import React, { useState, useEffect } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress, 
  OutlinedInput, 
  Box, 
  Chip 
} from '@mui/material';
import { projectsApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { PROJECT_PURPOSE_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

// Legacy export for backward compatibility
export const RETURN_VALUE_TYPES = PROJECT_PURPOSE_RETURN_TYPES;

const ProjectPurposeSelector = ({ 
  multiple = false,
  value = multiple ? [] : '', 
  onChange, 
  label = "Project Purpose",
  required = false,
  disabled = false,
  fullWidth = true,
  returnValue = PROJECT_PURPOSE_RETURN_TYPES.ID, // PROJECT_PURPOSE_RETURN_TYPES.ID or PROJECT_PURPOSE_RETURN_TYPES.NAME
  showEmptyOption = true,
  emptyOptionLabel = "None",
  size = 'medium',
  orgUnitTypeId = null, // New prop: if provided, fetches purposes 
  ...props 
}) => {
  const [projectPurposes, setProjectPurposes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showAlert } = useAlert();

  useEffect(() => {
    const fetchProjectPurposes = async () => {
      try {
        setIsLoading(true);
        
        if (orgUnitTypeId) {
          // Fetch purposes filtered by organization unit type
          const purposesData = await projectsApi.getProjectPurposesByOrgUnitType(orgUnitTypeId);
          setProjectPurposes(purposesData);
        } else {
          // No business unit selected 
          const purposesData = await projectsApi.getProjectPurposes();
          setProjectPurposes(purposesData);
        }
      } catch (error) {
        showAlert('Failed to load project purposes', 'error', error);
        setProjectPurposes([]); // Set empty array on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectPurposes();
  }, [showAlert, orgUnitTypeId]);

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    
    if (onChange) {
      if (multiple) {
        if (returnValue === PROJECT_PURPOSE_RETURN_TYPES.NAME) {
          const selectedNames = selectedValue.map(id => {
            const purpose = projectPurposes.find(p => p.id === id);
            return purpose?.name || '';
          }).filter(Boolean);
          onChange(selectedNames);
        } else {
          onChange(selectedValue);
        }
      } else {
        if (returnValue === PROJECT_PURPOSE_RETURN_TYPES.NAME) {
          const purpose = projectPurposes.find(p => p.id === selectedValue);
          onChange(purpose?.name || '');
        } else {
          onChange(selectedValue || '');
        }
      }
    }
  };

  // Find the selected project purpose(s) for internal Select component usage
  const selectedValue = (() => {
    if (!value) return multiple ? [] : '';
    
    if (multiple) {
      if (returnValue === PROJECT_PURPOSE_RETURN_TYPES.NAME) {
        return value.map(name => {
          const purpose = projectPurposes.find(p => p.name === name);
          return purpose?.id;
        }).filter(Boolean);
      } else {
        return Array.isArray(value) ? value : [];
      }
    } else {
      if (returnValue === PROJECT_PURPOSE_RETURN_TYPES.NAME) {
        const purpose = projectPurposes.find(p => p.name === value);
        return purpose?.id || '';
      } else {
        return value;
      }
    }
  })();

  const renderValue = (selected) => {
    if (multiple) {
      if (!selected || selected.length === 0) {
        return <em>{emptyOptionLabel}</em>;
      }
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((value) => {
            const purpose = projectPurposes.find(p => p.id === value);
            return (
              <Chip 
                key={value} 
                label={purpose?.name} 
                size="small"
              />
            );
          })}
        </Box>
      );
    }
    
    // Handle single select rendering
    if (!selected) return '';
    const purpose = projectPurposes.find(p => p.id === selected);
    return purpose?.name || '';
  };

  const isDisabled = disabled || isLoading;

  return (
    <FormControl fullWidth={fullWidth} required={required} disabled={isDisabled} size={size} {...props}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple={multiple}
        value={selectedValue}
        onChange={handleChange}
        label={label}
        disabled={isDisabled}
        input={multiple ? <OutlinedInput label={label} /> : undefined}
        renderValue={renderValue}
        endAdornment={
          isLoading ? <CircularProgress color="inherit" size={20} /> : null
        }
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          PaperProps: {
            style: {
              maxHeight: 300,
              zIndex: 1350, // Ensure dropdown appears above table
              marginTop: 8 // Add some space between input and dropdown
            }
          },
          // Prevent the menu from flipping to top when space is limited
          getContentAnchorEl: null
        }}
      >
        {/* Empty option for non-required fields */}
        {!required && !multiple && showEmptyOption && (
          <MenuItem value="">
            <em>{emptyOptionLabel}</em>
          </MenuItem>
        )}
        
        {projectPurposes.map(purpose => (
          <MenuItem key={purpose.id} value={purpose.id}>
            {purpose.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ProjectPurposeSelector; 